import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import shopIndentsApi from '../../services/shopIndentsApi';
import { billsApi } from '../../services/smartBillApi';
import {
  shopIndentToCartBillPrefill,
  shopIndentToSmartBillPayload,
  stashShopIndentCartPrefill,
} from '../../utils/shopIndentToCartBill';

const normStatus = (status) =>
  String(status || '')
    .trim()
    .toLowerCase();

const isOrderComplete = (status) =>
  ['paid', 'ready', 'delivered', 'completed'].includes(normStatus(status));

const statusLabel = (s) => {
  if (isOrderComplete(s)) return 'Order complete';
  const map = {
    pending: 'New order',
    quoted: 'Quoted — await pay',
    awaiting_payment: 'Awaiting payment',
    declined: 'Declined',
    customer_declined: 'Customer declined',
    customer_cancelled: 'Customer cancelled',
  };
  return map[normStatus(s)] || s || '—';
};

const canCollectPay = (status) =>
  ['quoted', 'awaiting_payment'].includes(normStatus(status));

const cardActionLabel = (status) => {
  const s = normStatus(status);
  if (isOrderComplete(s)) return 'View · Undo';
  if (canCollectPay(s)) return 'Collect pay';
  if (s === 'pending') return 'Review';
  return 'Open';
};

const orderWhenLabel = (row) => {
  if (!row) return '';
  const rel = String(row.createdAtRelative || '').trim();
  const abs = String(row.createdAtIst || '').trim();
  if (rel && abs && !rel.includes('IST')) return `${rel} · ${abs}`;
  if (abs) return abs;
  if (rel) return rel;
  const iso = row.createdAt || row.created_at;
  if (!iso) return '';
  try {
    return (
      new Date(iso).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      }) + ' IST'
    );
  } catch {
    return String(iso);
  }
};

/**
 * Mitra / AA Login — shop order inbox (parity with Flutter ShopIndentReview).
 * Approve → cart-style bill + UPI QR / GPay / PhonePe / WhatsApp pay request.
 */
const ShopOrdersInbox = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [finalAmount, setFinalAmount] = useState('');
  const [ownerNote, setOwnerNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [showPayPanel, setShowPayPanel] = useState(false);
  const [billError, setBillError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('upi');

  const mergeRow = useCallback((updated) => {
    if (!updated?.id) return;
    setRows((prev) => {
      const idx = prev.findIndex((r) => String(r.id) === String(updated.id));
      if (idx < 0) return [updated, ...prev];
      const next = prev.slice();
      next[idx] = { ...next[idx], ...updated };
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await shopIndentsApi.listBusiness();
      setRows(data);
      // Keep open dialog in sync with latest list status (no stale "Review").
      setSelected((cur) => {
        if (!cur?.id) return cur;
        const fresh = data.find((r) => String(r.id) === String(cur.id));
        return fresh ? { ...cur, ...fresh } : cur;
      });
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          'Could not load shop orders. Check Mitra shop scope / login.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = 'Shop Orders | LOCSY';
    load();
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, [load]);

  const filtered = useMemo(() => {
    if (tab === 'all') return rows;
    if (tab === 'active') {
      // In progress = not new, not finished
      return rows.filter((r) => canCollectPay(r.status));
    }
    if (tab === 'complete') {
      return rows.filter((r) => isOrderComplete(r.status));
    }
    if (tab === 'pending') {
      return rows.filter((r) => normStatus(r.status) === 'pending');
    }
    return rows.filter((r) => normStatus(r.status) === tab);
  }, [rows, tab]);

  const pendingCount = rows.filter(
    (r) => normStatus(r.status) === 'pending',
  ).length;

  const openReview = (row) => {
    setSelected(row);
    const deductions = Array.isArray(row.deductions) ? row.deductions : [];
    const couponOff = deductions.reduce(
      (s, d) => s + (Number(d?.amount) || 0),
      0,
    );
    if (row.finalAmountInr != null) {
      setFinalAmount(String(Number(row.finalAmountInr)));
    } else {
      setFinalAmount(
        String(Math.max(0, Number(row.subtotalInr || 0) - couponOff)),
      );
    }
    setOwnerNote(row.ownerNote || '');
    const savedPay = String(row.paymentMethod || row.payment_method || '')
      .trim()
      .toLowerCase();
    const fulfill = String(row.fulfillmentMode || row.fulfillment_mode || '')
      .trim()
      .toLowerCase();
    if (['cash', 'upi', 'card'].includes(savedPay)) {
      setPaymentMethod(savedPay);
    } else if (fulfill === 'at_shop' || fulfill === 'pickup') {
      setPaymentMethod('cash');
    } else {
      setPaymentMethod('upi');
    }
    setShowPayPanel(canCollectPay(row.status) && !isOrderComplete(row.status));
    setBillError(
      !row.billId && (row.billSyncError || row._billCreateError || row._warning)
        ? String(row.billSyncError || row._billCreateError || row._warning)
        : '',
    );
  };

  const closeReview = () => {
    if (busy) return;
    setSelected(null);
    setShowPayPanel(false);
    setBillError('');
  };

  const runAction = async (action, extra = {}, { keepOpen = false } = {}) => {
    if (!selected?.id) return null;
    setBusy(true);
    try {
      const updated = await shopIndentsApi.mitraAction(selected.id, action, {
        ownerNote: ownerNote.trim() || undefined,
        ...extra,
      });
      if (updated) {
        setSelected(updated);
        mergeRow(updated);
        if (updated.finalAmountInr != null) {
          setFinalAmount(String(Number(updated.finalAmountInr)));
        }
        const errText =
          !updated.billId &&
          (updated.billSyncError ||
            updated._billCreateError ||
            updated._warning)
            ? String(
                updated.billSyncError ||
                  updated._billCreateError ||
                  updated._warning,
              )
            : '';
        setBillError(errText);
      }
      if (action === 'sync_bill') {
        if (updated?.billId) {
          setBillError('');
          toast.success('Bill saved — check View Bills / sales totals');
        } else {
          const err =
            updated?._billCreateError ||
            updated?._warning ||
            updated?.billSyncError ||
            'Could not save bill';
          setBillError(String(err));
          toast.error(String(err), { autoClose: 12000 });
        }
      } else if (keepOpen) {
        toast.success(
          action === 'quote'
            ? 'Bill ready — show QR or send pay request'
            : 'Updated',
        );
        if (action === 'quote') setTab('active');
      } else if (action === 'confirm_payment') {
        if (updated?.billId) {
          setBillError('');
          toast.success('Order complete — bill added to View Bills / sales');
        } else {
          const err =
            updated?._billCreateError ||
            updated?._warning ||
            updated?.billSyncError ||
            'Order complete, but bill was not created. Tap “Save to View Bills”.';
          setBillError(String(err));
          toast.error(String(err), { autoClose: 12000 });
        }
        setShowPayPanel(false);
        setTab('complete');
      } else if (action === 'undo_payment') {
        toast.info('Undone — order reopened for payment');
        setShowPayPanel(canCollectPay(updated?.status));
        setTab('active');
      } else {
        toast.success('Updated');
        setSelected(null);
        setShowPayPanel(false);
      }
      // Refresh in background — list already patched via mergeRow
      load();
      return updated;
    } catch (e) {
      const msg =
        e?.response?.data?.billCreateError ||
        e?.response?.data?.message ||
        e?.message ||
        'Action failed';
      setBillError(String(msg));
      toast.error(String(msg), { autoClose: 12000 });
      if (
        /upi/i.test(msg) ||
        e?.response?.data?.code === 'UPI_REQUIRED'
      ) {
        toast.info('Set Shop UPI in Payment Settings, then try again.');
      }
      return null;
    } finally {
      setBusy(false);
    }
  };

  const approveQuote = async () => {
    const amount = Number(finalAmount);
    if (!(amount >= 0)) {
      toast.error('Enter a valid amount');
      return;
    }
    const deductions = Array.isArray(selected?.deductions)
      ? selected.deductions
      : [];
    const updated = await runAction(
      'quote',
      {
        finalAmountInr: amount,
        deductions,
        items: selected.items,
      },
      { keepOpen: true },
    );
    // Compulsory: open Cart Bill form with offer + coupon ready (no verify-payment shortcut).
    if (updated && (canCollectPay(updated.status) || updated.status === 'quoted')) {
      openCartBillForm(updated);
    }
  };

  /** Compulsory Cart Model Bill — offer product + coupon discount prefilled. */
  const openCartBillForm = (row = selected) => {
    if (!row?.id) return;
    const amountFromOpenForm =
      selected?.id === row.id && Number(finalAmount) >= 0
        ? Number(finalAmount)
        : null;
    const withAmount = {
      ...row,
      finalAmountInr:
        amountFromOpenForm != null
          ? amountFromOpenForm
          : row.finalAmountInr != null
            ? Number(row.finalAmountInr)
            : row.finalAmountInr,
    };
    const prefill = shopIndentToCartBillPrefill(withAmount);
    stashShopIndentCartPrefill(prefill);
    closeReview();
    navigate('/bills/cart-model', {
      state: { shopIndentCartPrefill: prefill },
    });
  };

  /** One-click: same POST /smartbills/bills as Cart Bill Create, then link shop order. */
  const confirmViaCartBillApi = async () => {
    if (!selected?.id) return;
    setBusy(true);
    setBillError('');
    try {
      const withAmount = {
        ...selected,
        finalAmountInr:
          Number(finalAmount) >= 0
            ? Number(finalAmount)
            : selected.finalAmountInr,
      };
      const payload = shopIndentToSmartBillPayload(withAmount);
      const response = await billsApi.create(payload);
      if (response?.success === false) {
        throw new Error(
          response?.error || response?.message || 'Cart bill create failed',
        );
      }
      const created = response?.data || response || {};
      const linkedBillId = created.id || created.bill_id;
      if (!linkedBillId) {
        throw new Error(
          'Bill API returned no id — check Smart Bills / Cart Bill create',
        );
      }
      const updated = await shopIndentsApi.mitraAction(
        selected.id,
        'confirm_payment',
        {
          linkedBillId,
          paymentMethod,
          paymentMethods: {
            cash: paymentMethod === 'cash' ? Number(finalAmount || 0) : 0,
            upi: paymentMethod === 'upi' ? Number(finalAmount || 0) : 0,
            card: paymentMethod === 'card' ? Number(finalAmount || 0) : 0,
          },
        },
      );
      setSelected(updated);
      mergeRow(updated);
      setBillError('');
      setShowPayPanel(false);
      setTab('complete');
      toast.success(
        `Order complete — bill ${created.bill_number || linkedBillId} in View Bills`,
      );
      load();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        e?.error ||
        'Could not create bill via Cart Bill API';
      setBillError(String(msg));
      toast.error(String(msg), { autoClose: 12000 });
    } finally {
      setBusy(false);
    }
  };

  const payBundle = useMemo(() => {
    if (!selected) return null;
    const amount =
      Number(finalAmount) >= 0
        ? Number(finalAmount)
        : Number(selected.finalAmountInr || selected.subtotalInr || 0);
    const upiId = String(selected.upiId || '').trim();
    const upiUri = shopIndentsApi.buildUpiUri({
      upiId,
      payeeName: selected.businessName || 'Loksy Shop',
      amount,
      tokenNo: selected.tokenNo || selected.payQNo,
      indentId: selected.id,
    });
    const links = shopIndentsApi.paymentAppLinks(upiUri);
    const qrUrl = upiUri
      ? shopIndentsApi.buildClickablePayQrUrl(upiUri, 280)
      : '';
    return {
      amount,
      upiId,
      upiUri,
      qrUrl,
      links,
      publicPay: shopIndentsApi.publicPayUrl(selected.id),
      payApp: shopIndentsApi.payAppLabel(selected),
    };
  }, [selected, finalAmount]);

  const copyPayMsg = async () => {
    if (!selected) return;
    const text = shopIndentsApi.whatsappShareText({
      ...selected,
      finalAmountInr: Number(finalAmount) || selected.finalAmountInr,
    });
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Payment message copied (with tap-to-pay link)');
    } catch {
      toast.error('Could not copy — use WhatsApp send instead');
    }
  };

  const whatsappPay = async () => {
    if (!selected) return;
    const text = encodeURIComponent(
      shopIndentsApi.whatsappShareText({
        ...selected,
        finalAmountInr: Number(finalAmount) || selected.finalAmountInr,
      }),
    );
    const phone = shopIndentsApi.payRequestWhatsAppDigits(selected);
    if (phone.length === 10) {
      window.open(`https://wa.me/91${phone}?text=${text}`, '_blank');
    } else {
      window.open(`https://wa.me/?text=${text}`, '_blank');
    }
    await runAction('mark_pay_request_sent', {}, { keepOpen: true });
  };

  const couponLines = Array.isArray(selected?.deductions)
    ? selected.deductions.filter((d) => Number(d?.amount) > 0 || d?.couponId)
    : [];
  const subtotal = Number(selected?.subtotalInr || 0);
  const couponOff = couponLines.reduce(
    (s, d) => s + (Number(d?.amount) || 0),
    0,
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 960, mx: 'auto' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800}>
            Shop Orders
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Citizen app + web redeem orders. Accept → bill + payment QR → confirm
            payment (coupon burns only on confirm).
          </Typography>
        </Box>
        <Button variant="contained" onClick={load} sx={{ bgcolor: '#FF6A00' }}>
          Refresh{pendingCount ? ` (${pendingCount} new)` : ''}
        </Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
        Legal tip: treat coupon discounts as shop offers. Confirm payment only after you
        receive UPI. Declining an order does not delete the customer&apos;s coupon unless
        you already marked it used elsewhere.
      </Alert>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        <Tab value="pending" label={`New (${pendingCount})`} />
        <Tab value="active" label="In progress" />
        <Tab value="complete" label="Complete" />
        <Tab value="all" label="All" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" action={<Button onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      ) : filtered.length === 0 ? (
        <Alert severity="warning">No orders in this view yet.</Alert>
      ) : (
        <Stack spacing={1.5}>
          {filtered.map((row) => {
            const items = Array.isArray(row.items) ? row.items : [];
            const names = items
              .map((i) => {
                if (!i?.name) return null;
                return i.isNotListed || i.pricePending
                  ? `${i.name} (not listed)`
                  : i.name;
              })
              .filter(Boolean)
              .join(', ');
            const couponChip = Array.isArray(row.deductions)
              ? row.deductions.find((d) => Number(d?.amount) > 0 || d?.couponId)
              : null;
            return (
              <Card key={row.id} variant="outlined">
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Typography fontWeight={800}>
                      {row.customerName || 'Customer'} ·{' '}
                      {row.customerPhone || ''}
                    </Typography>
                    <Chip
                      size="small"
                      color={isOrderComplete(row.status) ? 'success' : 'default'}
                      label={statusLabel(row.status)}
                    />
                  </Stack>
                  {row.tokenNo || row.payQNo || orderWhenLabel(row) ? (
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="primary"
                      display="block"
                      sx={{ mt: 0.25 }}
                    >
                      {row.tokenNo
                        ? `Token ${row.tokenNo}`
                        : row.payQNo
                          ? `Pay Q ${row.payQNo}`
                          : ''}
                      {(row.tokenNo || row.payQNo) && orderWhenLabel(row)
                        ? ' · '
                        : ''}
                      {orderWhenLabel(row)}
                    </Typography>
                  ) : null}
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {names || 'Items'}
                  </Typography>
                  <Typography fontWeight={700}>
                    Est. ₹{Number(row.subtotalInr || 0).toFixed(0)}
                    {row.finalAmountInr != null
                      ? ` · Bill ₹${Number(row.finalAmountInr).toFixed(0)}`
                      : ''}
                  </Typography>
                  {couponChip ? (
                    <Chip
                      size="small"
                      color="secondary"
                      sx={{ mt: 1 }}
                      label={`Coupon −₹${Number(couponChip.amount || 0).toFixed(0)} ${
                        couponChip.couponCode || ''
                      }`}
                    />
                  ) : null}
                  <Button
                    size="small"
                    variant={isOrderComplete(row.status) ? 'outlined' : 'contained'}
                    color={isOrderComplete(row.status) ? 'success' : 'primary'}
                    sx={{
                      mt: 1.5,
                      ...(isOrderComplete(row.status)
                        ? {}
                        : { bgcolor: '#FF6A00', '&:hover': { bgcolor: '#e65f00' } }),
                    }}
                    onClick={() => {
                      // Quoted / awaiting pay → Cart Bill form (compulsory; no verify shortcut).
                      if (canCollectPay(row.status)) {
                        openCartBillForm(row);
                        return;
                      }
                      openReview(row);
                    }}
                  >
                    {canCollectPay(row.status)
                      ? 'Open Cart Bill'
                      : cardActionLabel(row.status)}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Dialog
        open={!!selected}
        onClose={closeReview}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {isOrderComplete(selected?.status)
            ? 'Order complete'
            : showPayPanel && canCollectPay(selected?.status)
              ? 'Cart bill · Collect payment'
              : 'Review order'}
        </DialogTitle>
        <DialogContent>
          {selected && (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <Typography fontWeight={700}>
                {selected.customerName} · {selected.customerPhone}
              </Typography>
              <Typography variant="caption" fontWeight={800} color="#EA580C">
                {selected.tokenNo
                  ? `Token ${selected.tokenNo}`
                  : selected.payQNo
                    ? `Pay Q ${selected.payQNo}`
                    : 'Order'}
                {orderWhenLabel(selected)
                  ? ` · ${orderWhenLabel(selected)}`
                  : ''}
                {selected.customerPayApp
                  ? ` · Pay app: ${shopIndentsApi.payAppLabel(selected)}`
                  : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selected.note || 'No customer note'}
              </Typography>
              {isOrderComplete(selected.status) && (
                <Alert
                  severity={selected.billId ? 'success' : 'warning'}
                  sx={{ fontSize: 13 }}
                >
                  {selected.billId
                    ? 'Order complete — saved in View Bills (paid). Use Undo only if this was a mistake.'
                    : 'Order complete, but not in View Bills yet. Tap “Save to View Bills” so sales totals update.'}
                </Alert>
              )}
              {(billError ||
                (!selected.billId && selected.billSyncError)) && (
                <Alert severity="error" sx={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>
                  <Typography fontWeight={800} variant="body2" sx={{ mb: 0.5 }}>
                    Why bill was not created
                  </Typography>
                  {billError || selected.billSyncError}
                </Alert>
              )}
              <Divider />

              {/* Cart-style bill lines */}
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                }}
              >
                <Typography fontWeight={800} sx={{ mb: 1 }}>
                  Bill
                </Typography>
                {(selected.items || []).map((it, idx) => {
                  const qty = Number(it.quantity || 1);
                  const unit = Number(it.unitPrice || 0);
                  const notListed = Boolean(it.isNotListed || it.pricePending);
                  return (
                    <Stack
                      key={idx}
                      direction="row"
                      justifyContent="space-between"
                      sx={{
                        mb: 0.5,
                        ...(notListed
                          ? {
                              bgcolor: '#fffbeb',
                              borderRadius: 1,
                              px: 1,
                              py: 0.5,
                              border: '1px solid #fbbf24',
                            }
                          : {}),
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={
                          notListed
                            ? { color: '#b45309', fontWeight: 700 }
                            : undefined
                        }
                      >
                        {qty}× {it.name}
                        {notListed
                          ? unit > 0
                            ? ` @ ₹${unit.toFixed(0)} (not listed)`
                            : ' · shop sets price'
                          : ` @ ₹${unit.toFixed(0)}`}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={notListed ? { color: '#b45309' } : undefined}
                      >
                        {notListed && unit <= 0 ? '—' : `₹${(qty * unit).toFixed(0)}`}
                      </Typography>
                    </Stack>
                  );
                })}
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Subtotal
                  </Typography>
                  <Typography variant="body2">₹{subtotal.toFixed(0)}</Typography>
                </Stack>
                {couponLines.map((d, i) => (
                  <Stack
                    key={i}
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Typography variant="body2" color="success.main">
                      {d.label || d.couponCode || 'Coupon'}
                    </Typography>
                    <Typography variant="body2" color="success.main" fontWeight={700}>
                      −₹{Number(d.amount || 0).toFixed(0)}
                    </Typography>
                  </Stack>
                ))}
                {!couponLines.length && couponOff > 0 ? (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="success.main">
                      Coupon
                    </Typography>
                    <Typography variant="body2" color="success.main" fontWeight={700}>
                      −₹{couponOff.toFixed(0)}
                    </Typography>
                  </Stack>
                ) : null}
                <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                  <Typography fontWeight={800}>Customer pays</Typography>
                  <Typography fontWeight={800} color="#EA580C">
                    ₹
                    {(
                      Number(finalAmount) >= 0
                        ? Number(finalAmount)
                        : Math.max(0, subtotal - couponOff)
                    ).toFixed(0)}
                  </Typography>
                </Stack>
              </Box>

              {selected.status === 'pending' && (
                <TextField
                  label="Note to customer (optional)"
                  value={ownerNote}
                  onChange={(e) => setOwnerNote(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  helperText="Order list is as placed by the customer. Ask them to update if items must change."
                />
              )}

              {/* Payment QR panel — Flutter Collect payment / cart-bill parity */}
              {showPayPanel &&
                canCollectPay(selected.status) &&
                payBundle && (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: '#eefdf5',
                      textAlign: 'center',
                      border: '1px solid #bbf7d0',
                    }}
                  >
                    <Typography fontWeight={800}>Collect payment</Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="center"
                      sx={{ mt: 1.5 }}
                    >
                      {['cash', 'upi', 'card'].map((m) => (
                        <Button
                          key={m}
                          size="small"
                          variant={paymentMethod === m ? 'contained' : 'outlined'}
                          onClick={() => setPaymentMethod(m)}
                          sx={
                            paymentMethod === m
                              ? { bgcolor: '#166534' }
                              : undefined
                          }
                        >
                          {m === 'cash' ? 'Cash' : m === 'card' ? 'Card' : 'UPI'}
                        </Button>
                      ))}
                    </Stack>
                    {paymentMethod === 'cash' && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 1 }}
                      >
                        Collect cash at the counter, then confirm Cash received.
                      </Typography>
                    )}
                    {paymentMethod === 'card' && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 1 }}
                      >
                        Swipe / tap card, then confirm Card received.
                      </Typography>
                    )}
                    {paymentMethod === 'upi' && (
                      <Typography fontWeight={800} sx={{ mt: 1.5 }}>
                        Payment QR
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      fontWeight={800}
                      color="#EA580C"
                      sx={{ mt: 0.5 }}
                    >
                      {selected.tokenNo
                        ? `Token ${selected.tokenNo} · `
                        : selected.payQNo
                          ? `Pay Q ${selected.payQNo} · `
                          : ''}
                      ₹{payBundle.amount.toFixed(0)}
                    </Typography>

                    {paymentMethod === 'upi' &&
                      (payBundle.qrUrl ? (
                      <>
                        <Box
                          component="img"
                          src={payBundle.qrUrl}
                          alt="Payment QR"
                          sx={{
                            display: 'block',
                            mx: 'auto',
                            mt: 1.5,
                            width: 200,
                            height: 200,
                            borderRadius: 1,
                            bgcolor: '#fff',
                            border: '1px solid #e2e8f0',
                          }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mt: 0.5 }}
                        >
                          UPI: {payBundle.upiId}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Customer scans at counter, or tap app buttons on phone
                        </Typography>

                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          sx={{ mt: 1.5 }}
                          justifyContent="center"
                        >
                          <Button
                            size="small"
                            variant="contained"
                            href={payBundle.links.gpay || payBundle.links.any}
                            sx={{ bgcolor: '#4285F4' }}
                          >
                            Google Pay
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            href={payBundle.links.phonePe || payBundle.links.any}
                            sx={{ bgcolor: '#5F259F' }}
                          >
                            PhonePe
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            href={payBundle.links.any}
                          >
                            Any UPI
                          </Button>
                        </Stack>
                      </>
                    ) : (
                      <Alert severity="warning" sx={{ mt: 1, textAlign: 'left' }}>
                        Shop UPI missing on this bill. Set UPI in Payment Settings,
                        then Approve again (or reopen after UPI is saved).
                      </Alert>
                    ))}

                    {paymentMethod === 'upi' && (
                      <>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      sx={{ mt: 2 }}
                      justifyContent="center"
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={busy}
                        onClick={copyPayMsg}
                      >
                        Copy pay msg
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        disabled={busy}
                        onClick={whatsappPay}
                        sx={{ bgcolor: '#5F259F' }}
                      >
                        Send pay request (WhatsApp)
                      </Button>
                    </Stack>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      WhatsApp opens {payBundle.payApp} tap-to-pay link for customer
                    </Typography>
                      </>
                    )}
                  </Box>
                )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
          {isOrderComplete(selected?.status) && !selected?.billId && (
            <Button
              variant="contained"
              color="success"
              disabled={busy}
              onClick={() => openCartBillForm(selected)}
            >
              Open Cart Bill form
            </Button>
          )}
          {isOrderComplete(selected?.status) && (
            <Button
              variant="outlined"
              color="primary"
              disabled={busy}
              onClick={() => {
                const phone = String(selected?.customerPhone || '')
                  .replace(/\D/g, '')
                  .slice(-10);
                closeReview();
                navigate(
                  phone
                    ? `/bills/view?search=${encodeURIComponent(phone)}&range=thisYear`
                    : '/bills/view?range=thisYear',
                );
              }}
            >
              View Bills
            </Button>
          )}
          <Button disabled={busy} onClick={closeReview}>
            Close
          </Button>
          {normStatus(selected?.status) === 'pending' && (
            <>
              <Button
                color="error"
                disabled={busy}
                onClick={() => runAction('decline')}
              >
                Decline
              </Button>
              <Button
                variant="contained"
                disabled={busy}
                onClick={approveQuote}
                sx={{ bgcolor: '#FF6A00' }}
              >
                Approve & open Cart Bill · ₹
                {(Number(finalAmount) >= 0 ? Number(finalAmount) : 0).toFixed(0)}
              </Button>
            </>
          )}
          {canCollectPay(selected?.status) && (
            <>
              {!showPayPanel && (
                <Button
                  disabled={busy}
                  onClick={() => setShowPayPanel(true)}
                >
                  Collect Cash / UPI
                </Button>
              )}
              <Button
                variant="contained"
                color="success"
                disabled={busy}
                onClick={() => {
                  const label =
                    paymentMethod === 'cash'
                      ? 'Cash'
                      : paymentMethod === 'card'
                        ? 'Card'
                        : 'UPI';
                  if (
                    window.confirm(
                      `Confirm ${label} received for ₹${Number(finalAmount || 0).toFixed(0)}?`,
                    )
                  ) {
                    const amt = Number(finalAmount || 0);
                    runAction('confirm_payment', {
                      paymentMethod,
                      paymentMethods: {
                        cash: paymentMethod === 'cash' ? amt : 0,
                        upi: paymentMethod === 'upi' ? amt : 0,
                        card: paymentMethod === 'card' ? amt : 0,
                      },
                    });
                  }
                }}
              >
                {paymentMethod === 'cash'
                  ? 'Cash received — confirm'
                  : paymentMethod === 'card'
                    ? 'Card received — confirm'
                    : 'UPI received — confirm'}
              </Button>
              <Button
                variant="outlined"
                color="success"
                disabled={busy}
                onClick={() => openCartBillForm(selected)}
              >
                Open Cart Bill form
              </Button>
            </>
          )}
          {isOrderComplete(selected?.status) && (
            <Button
              color="warning"
              disabled={busy}
              onClick={() => {
                if (
                  window.confirm(
                    'Undo payment confirmation? Order reopens for pay, coupon is restored if linked, and the View Bills entry is cancelled.',
                  )
                ) {
                  runAction('undo_payment');
                }
              }}
            >
              Undo (mistake)
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShopOrdersInbox;
